import React, { useEffect, useState } from "react";
import Button from "../../../forms/Button";
import Input from "../../../form/Input";
import { changePassword } from "../../../../services/authService";
import { toast } from "react-toastify";
import { Validation } from "../../../../helpers/Validation";

const ChangePassword = () => {
  const [isPasswordEditable, setPasswordEditable] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [passwordStrength, setPasswordStrength] = useState({
    label: "",
    color: "",
    width: "0%",
  });
  const [loading, setLoading] = useState(false);

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push("At least 8 characters");
    if (!/[A-Z]/.test(password)) errors.push("One uppercase letter");
    if (!/[a-z]/.test(password)) errors.push("One lowercase letter");
    if (!/\d/.test(password)) errors.push("One digit");
    if (!/[!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]/.test(password)) errors.push("One special character");
    return errors;
  };

  const calculateStrength = (password) => {
    const errors = validatePassword(password);
    const score = 5 - errors.length;
    if (score <= 2) return { label: "Weak", color: "#f87171", width: "33%" };
    if (score === 3 || score === 4) return { label: "Medium", color: "#fbbf24", width: "66%" };
    return { label: "Strong", color: "#4ade80", width: "100%" };
  };

  useEffect(() => {
    if (newPassword) {
      setPasswordStrength(calculateStrength(newPassword));
    } else {
      setPasswordStrength({ label: "", color: "", width: "0%" });
    }
  }, [newPassword]);

  const handleSave = async () => {
    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      toast.error("Password must include: " + passwordErrors.join(", "));
      return;
    }

    if (!Validation("confirmPassword", confirmPassword, newPassword)) {
      toast.error("Confirm password doesn't match.");
      return;
    }

    try {
      setLoading(true);
      const response = await changePassword({
        oldPassword: currentPassword,
        newPassword: newPassword,
        confirmPassword: confirmPassword,
      });

      if (response.status === 200) {
        toast.success("Password updated successfully!");
        setPasswordEditable(false);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        console.log(response);
        toast.error(response?.message || "Failed to update password.");
      }
    } catch (error) {
      console.log(error);
      toast.error(error?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="relative bg-white py-4 px-4 shadow shadow-md-pink/20 rounded-lg">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
          <div className="w-full max-w-2xl space-y-1">
            <div className="text-lg 2xl:text-xl text-md-dark font-semibold">Change password</div>
            <div className="text-sm text-slate-500 tracking-wide">Update your password associated with your account.</div>
          </div>
          <div className="ml-auto flex-shrink-0 flex items-center gap-1">
            {isPasswordEditable ? (
              <>
                <Button
                  buttonHasLink={false}
                  buttonIcon={"fa-regular fa-fw fa-xmark"}
                  buttonIconPosition={"left"}
                  buttonLabel={"Cancel"}
                  buttonClasses={"!h-auto !rounded !px-2 !py-1 !text-md-pink !bg-transparent hover:!bg-md-pink/10 !gap-1"}
                  buttonLabelClasses={"!font-semibold !text-md-dark !text-sm"}
                  buttonFunction={() => setPasswordEditable(false)}
                />
                <Button
                  buttonHasLink={false}
                  buttonIcon={"fa-regular fa-fw fa-floppy-disk"}
                  buttonIconPosition={"left"}
                  buttonLabel={"Save"}
                  buttonClasses={"!h-auto !rounded !px-2 !py-1 !text-md-pink !bg-transparent hover:!bg-md-pink/10 !gap-1"}
                  buttonLabelClasses={"!font-semibold !text-md-dark !text-sm"}
                  buttonFunction={handleSave}
                  isDisable={loading}
                />
              </>
            ) : (
              <Button
                buttonHasLink={false}
                buttonIcon={"fa-regular fa-fw fa-pen-to-square"}
                buttonIconPosition={"left"}
                buttonLabel={"Change Password"}
                buttonClasses={"!h-auto !rounded !px-2 !py-1 !text-md-pink !bg-transparent hover:!bg-md-pink/10 !gap-1"}
                buttonLabelClasses={"!font-semibold !text-md-dark !text-sm"}
                buttonFunction={() => setPasswordEditable(true)}
              />
            )}
          </div>
        </div>
        {isPasswordEditable && (
          <div className="mt-4 pt-4 border-t border-md-pink-light-1">
            <div className="relative space-y-6 max-w-md">
              <div className="relative">
                <Input label={"Current Password"} labelClasses={"text-sm 2xl:text-base"} inputType={"password"} inputPlaceholder={"Enter current password"} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
              </div>
              <div className="relative">
                <Input label={"New Password"} labelClasses={"text-sm 2xl:text-base"} inputType={"password"} inputPlaceholder={"Enter new password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                <div className="mt-4 space-y-2">
                  <div className="relative overflow-hidden rounded-full h-2 bg-slate-100">
                    <div
                      className="absolute top-0 left-0 h-full rounded-full transition-all duration-300"
                      style={{
                        backgroundColor: passwordStrength.color,
                        width: passwordStrength.width,
                      }}
                    />
                  </div>
                  {passwordStrength.label && <div className="text-neutral-500 text-sm font-medium">Password strength: {passwordStrength.label}</div>}
                </div>
              </div>
              <div className="relative">
                <Input label={"Confirm Password"} labelClasses={"text-sm 2xl:text-base"} inputType={"password"} inputPlaceholder={"Confirm new password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-md-pink-light-1 flex items-start justify-between gap-4">
              <div className="ml-auto flex-shrink-0 flex items-center gap-1 md:gap-2">
                <Button
                  buttonHasLink={false}
                  buttonIcon={"fa-regular fa-fw fa-xmark"}
                  buttonIconPosition={"left"}
                  buttonLabel={"Cancel"}
                  buttonClasses={"!h-10 !rounded-md !px-3 !bg-md-pink-light-2 border border-md-pink-light-1 !text-md-pink hover:!bg-md-pink-light-1 hover:border-md-pink/20"}
                  buttonLabelClasses={"!font-medium !pb-1 !leading-none"}
                  buttonFunction={() => setPasswordEditable(false)}
                />
                <Button buttonHasLink={false} buttonIcon={"fa-regular fa-fw fa-floppy-disk"} buttonIconPosition={"left"} buttonLabel={"Save"} isDisable={loading} buttonClasses={"!h-10 !rounded-md !px-3"} buttonLabelClasses={"!font-medium !pb-1 !leading-none"} buttonFunction={handleSave} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ChangePassword;
